<template>
  <!-- biome-ignore lint/a11y/noStaticElementInteractions: hover only animates the images, nothing to reach by keyboard -->
  <!-- biome-ignore lint/a11y/noNoninteractiveElementInteractions: hover only animates the images, nothing to reach by keyboard -->
  <div
    class="single-obituary-comment"
    :class="{ 'use-fallback-svg': showFallbackSvg }"
    :id="`comment-${comment.id}`"
    @mouseenter="handleMouseenter"
    @mouseleave="handleMouseleave"
  >
    <div class="single-obituary-comment__container">
      <div>
        <loader :loading="loading && uploading" />
        <div
          class="single-obituary-comment__image-place"
          :class="{
            inactive: !thumbnail || isMobile,
            'no-image': !thumbnail,
          }"
        >
          <svg
            aria-hidden="true"
            class="single-obituary-comment__fallback-svg"
            v-if="showFallbackSvg"
          >
            <use :href="fallbackSvgHref" />
          </svg>
          <div
            class="single-obituary-comment__pending-media"
            v-if="hasPendingMedia()"
          >
            <h4 v-text="texts.pendingMediaHeading" />
            <div class="single-obituary-comment__pending-media__content">
              <span v-text="texts.pendingMediaMessage" />
              <svg
                aria-hidden="true"
                class="icon icon--3x"
                :style="{ fill: '#333' }"
              >
                <use href="#photo-film" />
              </svg>
            </div>
          </div>
          <div class="single-obituary-comment__image-place__container" v-else>
            <!-- biome-ignore lint/a11y/useKeyWithClickEvents: pre-existing mouse-only gallery opener, keyboard access is out of scope here -->
            <!-- biome-ignore lint/a11y/noStaticElementInteractions: pre-existing mouse-only gallery opener, keyboard access is out of scope here -->
            <!-- biome-ignore lint/a11y/noNoninteractiveElementInteractions: pre-existing mouse-only gallery opener, keyboard access is out of scope here -->
            <div @click.prevent="handleImageClick">
              <span
                class="single-obituary-comment__image-place__video-wrapper-play-button"
                v-if="!!firstVideo"
              >
                <svg
                  aria-hidden="true"
                  class="icon icon--4x"
                  :style="{ fill: 'white' }"
                >
                  <use href="#circle-play" />
                </svg>
              </span>
              <single-comment-images
                v-if="hasMedia"
                ref="singleCommentImages"
                :alt-text="altText"
                :comment="comment"
              />
              <span
                class="single-obituary-comment__image-icon multiple-media"
                v-if="hasMultipleMedia"
              >
                <svg aria-hidden="true" class="icon">
                  <use href="#images" />
                </svg>
              </span>
              <span
                class="single-obituary-comment__image-icon open-image"
                v-if="hasMedia"
              >
                <svg aria-hidden="true" class="icon">
                  <use href="#arrows-maximize" />
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="single-obituary-comment__content">
        <div class="single-obituary-comment__top-bar">
          <div class="name-wrapper">
            <p class="single-obituary-comment__name" v-html="comment.name" />
            <span class="single-obituary-comment__date" v-if="comment.date">
              {{ timeSinceDate(comment.date) }}</span
            >
          </div>

          <div class="single-obituary-comment__info" v-if="!preview">
            <div
              class="single-obituary-comment__donation"
              v-if="
                comment.donation ||
                comment.manualDonation ||
                comment.recurringDonation
              "
            >
              <button
                class="transparent"
                type="button"
                @click.prevent="toggleCreateCommentModal(DONATION_INITIAL)"
              >
                <svg aria-hidden="true" class="icon icon--large icon--hflip">
                  <use href="#hand-holding-heart" />
                </svg>
                <span
                  v-if="!comment.recurringDonation"
                  v-text="texts.hasDonated"
                />
                <span
                  v-if="comment.recurringDonation"
                  v-text="texts.hasMadeRecurringDonation"
                />
              </button>
            </div>

            <liked-comment-count :comment="comment" />
          </div>
        </div>
        <p
          class="single-obituary-comment__text single-obituary-comment__text--truncated"
          v-if="greetingPreview.truncated"
          ref="commentText"
          v-text="greetingPreview.text"
        />
        <p
          class="single-obituary-comment__text"
          v-else
          ref="commentText"
          v-html="comment.comment"
        />
        <button
          aria-haspopup="dialog"
          class="single-obituary-comment__show-more"
          type="button"
          v-if="greetingPreview.truncated"
          ref="showMoreButton"
          @click.prevent="openFullGreeting"
          v-text="texts.showMore"
        />

        <single-comment-actions
          v-if="!(preview || lavendlaComment)"
          :comment="comment"
          :deceased-name="deceasedName"
          :show-edit-buttons="!!(createdCommentsItem && !showVideo)"
          :toggle-modal="toggleCommentModal"
        />

        <threaded-comments-count
          v-if="!(preview || lavendlaComment)"
          :comment-count="threadedCommentsCount"
          :parent="comment"
        />

        <single-comment-threaded-comments
          v-if="!(preview || lavendlaComment)"
          :parent="comment"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { useDateFormatter } from "Composables/useDateFormatter";
  import { safeGetItem, safeSetItem } from "Utils/safeLocalStorage";
  import { ellipsis } from "ellipsed";
  import isString from "lodash-es/isString";
  import { sprintf } from "sprintf-js";
  import { toRef } from "vue";
  import { mapActions, mapGetters, mapState } from "vuex";
  import { useSingleCommentMedia } from "../composables/useSingleCommentMedia";
  import { DONATION_INITIAL } from "../createCommentStepsHelper";
  import { greetingToText, truncateGreeting } from "../greeting-preview";
  import LikedCommentCount from "./LikedCommentCount.vue";
  import Loader from "./Loader.vue";
  import SingleCommentActions from "./SingleCommentActions.vue";
  import SingleCommentImages from "./SingleCommentImages.vue";
  import SingleCommentThreadedComments from "./SingleCommentThreadedComments.vue";
  import ThreadedCommentsCount from "./ThreadedComments/Count.vue";

  export default {
    components: {
      LikedCommentCount,
      Loader,
      SingleCommentActions,
      SingleCommentImages,
      SingleCommentThreadedComments,
      ThreadedCommentsCount,
    },

    computed: {
      ...mapState("comments", ["uploading", "createdComments", "newCommentId"]),
      ...mapState("singleCommentModal", {
        openedNewCommentId: "openedNewCommentId",
        singleCommentModalOpen: "isOpen",
      }),
      ...mapGetters("global", ["isMobile"]),

      altText() {
        return sprintf(
          this.texts.imageAlt,
          this.comment.name,
          this.deceasedName
        );
      },

      createdCommentsItem() {
        if (!this.comment.children) {
          return null;
        }

        const [item] = this.createdComments.filter(
          (storedItem) => storedItem.id === this.comment.id
        );

        if (!item) {
          return null;
        }

        return item;
      },

      editItem() {
        return {
          comment: this.comment.comment,
          id: this.comment.id,
          name: this.comment.name,
        };
      },

      fallbackSvgHref() {
        return this.lavendlaComment
          ? "#memorial-comment-bird"
          : "#memorial-comment-candle";
      },

      // Previews (share and threaded comments modals) already clamp the text,
      // so only the card in the list is cut off.
      greetingPreview() {
        if (this.preview) {
          return { text: "", truncated: false };
        }

        return truncateGreeting(greetingToText(this.comment.comment));
      },

      hash() {
        if (this.createdCommentsItem) {
          return this.createdCommentsItem.hash;
        }

        return "";
      },

      lavendlaComment() {
        return !!(this.comment.id === "lavendla-comment");
      },

      showFallbackSvg() {
        return !this.hasMedia;
      },

      threadedCommentsCount() {
        return this.comment.children ? this.comment.children.length : 0;
      },
    },

    data() {
      return {
        DONATION_INITIAL,
        imgIxDomain: `https://${lavendlaObituary.imgIxDomain}/`,
        imgixParams: {
          ar: "53:32",
          crop: "faces,edges",
          fit: "crop",
        },
        index: null,
        returnFocusOnClose: false,
        showVideo: false,
        texts: lavendlaObituary.singleComment,
      };
    },

    methods: {
      ...mapActions({
        getObituaryComment: "comments/getObituaryComment",
        openSingleCommentModal: "singleCommentModal/open",
        setComment: "modal/setComment",
        toggleCreateCommentModal: "createComment/toggleModal",
        toggleModal: "modal/toggle",
        updateNewCommentId: "comments/updateNewCommentId",
      }),

      handleImageClick() {
        if (!this.hasMedia) {
          return;
        }

        this.setComment(this.comment);
        this.toggleModal();
      },

      handleMouseenter() {
        if (!this.$refs.singleCommentImages) {
          return;
        }

        this.$refs.singleCommentImages.animateImages();
      },

      handleMouseleave() {
        if (!this.$refs.singleCommentImages) {
          return;
        }

        this.$refs.singleCommentImages.animateImagesStop();
      },

      hasPendingMedia() {
        if (!this.comment.hasPendingMedia || this.firstImage) {
          return false;
        }

        if (this.firstVideo?.thumbnail) {
          return false;
        }

        return true;
      },

      openFullGreeting() {
        this.returnFocusOnClose = true;
        this.toggleCommentModal("read");
      },

      setIsNew() {
        // The post-create flow opens the share modal directly via the
        // comments/createObituaryComment store action. If that has already
        // happened for this comment in the current session, bail before doing
        // any further work — re-mounting SingleComment when the user toggles
        // between the timeline and masonry views must not re-open it.
        if (this.openedNewCommentId === this.comment.id) {
          return;
        }

        // The PennyBridge donation redirect uses newCommentId to flag the
        // donation's comment as "newly visible" so we open the share modal
        // for it. Once consumed we clear the flag so further re-mounts don't
        // re-trigger.
        if (this.newCommentId !== this.comment.id) {
          return;
        }

        this.updateNewCommentId(null);

        let { hash } = this;
        if (!hash) {
          hash = (Math.random() + 1).toString(36).slice(7);
        }

        if (safeGetItem(`new-comment-${hash}`)) {
          return;
        }

        safeSetItem(`new-comment-${hash}`, "true");
        this.openSingleCommentModal({
          comment: this.comment,
          context: "share",
          deceasedName: this.deceasedName,
          hash,
          isNew: true,
          memorialFund: this.memorialFund,
          possessiveName: this.possessiveName,
        });
      },

      toggleCommentModal(context = "") {
        this.openSingleCommentModal({
          comment: this.comment,
          context: isString(context) ? context : "",
          deceasedName: this.deceasedName,
          hash: this.hash,
          isNew: false,
          memorialFund: this.memorialFund,
          possessiveName: this.possessiveName,
        });
      },
    },

    mounted() {
      // If the comment is awaiting the media, check every 10 seconds
      // to see if Transloadit has finished and display the results here
      if (this.hasPendingMedia()) {
        setTimeout(() => this.getObituaryComment(this.comment.id), 1000 * 5);
      }

      if (this.preview) {
        ellipsis(this.$refs.commentText, 6, { responsive: true });
      }

      if (!this.preview) {
        // Wait for Masonry redraw
        setTimeout(() => {
          this.setIsNew();
        }, 1500);
      }
    },
    name: "SingleComment",

    props: {
      comment: {
        required: true,
        type: Object,
      },
      deceasedName: {
        default: "",
        type: String,
      },
      loading: {
        required: false,
        type: Boolean,
      },
      memorialFund: {
        default: null,
        type: Object,
      },
      openLightbox: {
        default: null,
        type: Function,
      },
      possessiveName: {
        default: "",
        type: String,
      },
      preview: {
        default: false,
        type: Boolean,
      },
      storedList: {
        default: null,
        type: Object,
      },
    },

    setup(props) {
      const { timeSinceDate, toValidDate, getLocale } = useDateFormatter();
      const commentRef = toRef(props, "comment");
      const {
        images,
        videos,
        firstVideo,
        firstImage,
        thumbnail,
        hasMultipleMedia,
        hasMedia,
        thumbnails,
      } = useSingleCommentMedia(commentRef);
      return {
        firstImage,
        firstVideo,
        getLocale,
        hasMedia,
        hasMultipleMedia,
        images,
        thumbnail,
        thumbnails,
        timeSinceDate,
        toValidDate,
        videos,
      };
    },

    watch: {
      singleCommentModalOpen(isOpen) {
        if (isOpen || !this.returnFocusOnClose) {
          return;
        }

        this.returnFocusOnClose = false;
        this.$refs.showMoreButton?.focus();
      },
    },
  };
</script>
